import React, { useEffect, useState } from 'react';
import type { TeamSecretDetail, TeamSecretPolicyV1 } from '../client';
import { useApi } from '../state/ApiContext';
import AsyncButton from '../components/AsyncButton';
import Modal from '../components/Modal';
import SecretPolicyEditor from './SecretPolicyEditor';
import { useGuardedCallback } from '../permissions/useGuardedAction';
import { SECRET_ACTION } from './secret-permissions';

interface SecretEditorProps {
  teamId: number;
  isOpen: boolean;
  secret?: TeamSecretDetail | null;
  embedded?: boolean;
  onClose: () => void;
  onSaved: (secret: TeamSecretDetail) => void;
  onConflict?: (message: string) => void | Promise<void>;
}

const emptyPolicy = (): TeamSecretPolicyV1 => ({
  version: 1,
  workspace: { enabled: true, allowedHosts: [] },
  browser: { enabled: false, allowedOrigins: [] },
});

export const normalizeSecretName = (name: string) =>
  name
    .trim()
    .normalize('NFKD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^A-Za-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '')
    .toUpperCase();

const errorMessage = (
  error: any,
  fallback = 'The secret could not be saved.',
) =>
  error?.response?.data?.error?.message ||
  error?.response?.data?.message ||
  fallback;

const SecretEditor: React.FC<SecretEditorProps> = ({
  teamId,
  isOpen,
  secret,
  embedded = false,
  onClose,
  onSaved,
  onConflict,
}) => {
  const { teamSecretsApi } = useApi();
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [value, setValue] = useState('');
  const [policy, setPolicy] = useState<TeamSecretPolicyV1>(emptyPolicy);
  const [retention, setRetention] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!isOpen) return;
    setName(secret?.currentName || '');
    setDescription(secret?.description || '');
    setPolicy(secret?.policy || emptyPolicy());
    setRetention(secret ? String(secret.priorVersionRetentionDays) : '');
    setValue('');
    setError('');
  }, [isOpen, secret?.id, secret?.revision]);

  const close = () => {
    setValue('');
    setError('');
    onClose();
  };

  const submit = async (event: React.FormEvent) => {
    event.preventDefault();
    setSaving(true);
    setError('');
    try {
      let saved: TeamSecretDetail;
      if (!secret) {
        const response = await teamSecretsApi.createTeamSecret(teamId, {
          name,
          description,
          value,
          policy,
          ...(retention === ''
            ? {}
            : { priorVersionRetentionDays: Number(retention) }),
        });
        saved = response.data;
      } else {
        const updated = await teamSecretsApi.updateTeamSecret(
          teamId,
          secret.id,
          {
            expectedRevision: secret.revision,
            name,
            description,
            policy,
            priorVersionRetentionDays: Number(retention),
          },
        );
        saved = updated.data;
      }
      setValue('');
      onSaved(saved);
      if (!embedded) onClose();
    } catch (requestError) {
      if (
        secret &&
        (requestError as any)?.response?.status === 409 &&
        (requestError as any)?.response?.data?.error?.code ===
          'REVISION_CONFLICT'
      ) {
        const message = errorMessage(requestError);
        if (onConflict) {
          await onConflict(message);
        } else {
          setError(
            `${message} Reload the latest changes, review them, and retry.`,
          );
        }
      } else {
        setError(errorMessage(requestError));
      }
    } finally {
      setSaving(false);
    }
  };

  /**
   * Create and change are the same form and the same requirement
   * (`TEAM_SECRET: manage`, from POST/PATCH `/api/teams/:teamId/secrets` — see
   * `secret-permissions.ts`); only the sentence differs, so a `view`-only
   * caller who opened this is told "create" or "change" rather than the wrong
   * one of the two.
   */
  const saveGuard = useGuardedCallback<[React.FormEvent]>({
    ...(secret ? SECRET_ACTION.EDIT : SECRET_ACTION.CREATE),
    testId: 'secret-save-denied',
    onAllowed: (event) => void submit(event),
  });

  const form = (
    <>
      <form
        className="secret-editor"
        onSubmit={(event) => {
          // Refuse before the native submit, not after it.
          event.preventDefault();
          saveGuard.run(event);
        }}>
        <div className="secret-form-grid">
          <label>
            Name
            <input
              type="text"
              value={name}
              maxLength={256}
              required
              onChange={(event) => setName(event.target.value)}
            />
          </label>
          <div className="secret-name-preview" aria-live="polite">
            <span>Normalized name</span>
            <code>{normalizeSecretName(name) || '—'}</code>
          </div>
        </div>
        {secret && (
          <p className="secret-warning">
            Workspace scripts and {'{{secret.NAME}}'} placeholders are not
            rewritten when a secret is renamed.
          </p>
        )}
        <label>
          Description
          <textarea
            rows={3}
            value={description}
            maxLength={4000}
            required
            onChange={(event) => setDescription(event.target.value)}
          />
        </label>
        {!secret && (
          <label>
            Secret value
            <input
              type="password"
              aria-label="Secret value"
              autoComplete="new-password"
              value={value}
              required
              onChange={(event) => setValue(event.target.value)}
            />
            <small>
              Write-only. The saved value cannot be viewed later and is never
              loaded into this field.
            </small>
          </label>
        )}
        <SecretPolicyEditor policy={policy} onChange={setPolicy} />
        <label className="secret-retention-override">
          {secret
            ? 'Prior version retention'
            : 'Prior version retention (optional)'}
          <span>
            <input
              aria-label={
                secret
                  ? 'Prior version retention'
                  : 'Prior version retention (optional)'
              }
              type="number"
              min={0}
              max={3650}
              value={retention}
              placeholder="Team default"
              onChange={(event) => setRetention(event.target.value)}
            />
            days
          </span>
        </label>
        {error && (
          <p className="secret-error" role="alert">
            {error}
          </p>
        )}
        <div className="secret-modal-actions">
          {!embedded && (
            <button
              type="button"
              className="lightButton"
              onClick={close}
              disabled={saving}>
              Cancel
            </button>
          )}
          <AsyncButton type="submit" loading={saving} loadingText="Saving…">
            {secret ? 'Save changes' : 'Create secret'}
          </AsyncButton>
        </div>
      </form>
      {saveGuard.denialModal}
    </>
  );

  if (embedded) return isOpen ? form : null;

  return (
    <Modal
      isOpen={isOpen}
      onClose={close}
      title={secret ? 'Edit secret' : 'Add secret'}
      maxWidth="720px"
      isBusy={saving}>
      {form}
    </Modal>
  );
};

export default SecretEditor;
